/**
 * Every chart in the product, and the only file that knows a colour.
 *
 * HAND-ROLLED RATHER THAN A CHART LIBRARY, AND THAT IS A DEPARTURE FROM THE PLAN, which
 * said Recharts behind this file. The reason the plan said "behind a single charts.tsx"
 * is that the palette has to live in one place, and that requirement is met either way.
 * What changed the answer is what actually needs drawing: four ranked bar lists, one
 * stacked column chart, a 7x24 heatmap and a funnel. The heatmap and the bar lists are
 * CSS grids and would fight a charting library rather than use it; that leaves one chart
 * out of six worth 120 KB and a second set of theming rules alongside the tokens. The
 * seam is here if that stops being true - the pages below import components, not SVG.
 *
 * A SEVENTH ARRIVED AND DID NOT CHANGE THE ANSWER. `SentimentPie` is the first `<svg>` in
 * this file, and it is three `<circle>` elements: a donut of three slices is not what a
 * charting library is for, and importing one to draw it would buy the 120 KB on the
 * strength of the one chart in the product that needs the least help.
 *
 * THE PALETTE COMES FROM THE PRODUCT'S DESIGN AND LIVES IN `@theme` in styles.css as
 * `--color-band-*` and `--color-heat-*`. It used to be the output of a six-check validator
 * covering colour-vision separation; it is not any more, and the note above the tokens says
 * so rather than leaving a claim behind that nobody can check.
 *
 * WHAT REPLACES THAT GUARANTEE IS THAT COLOUR IS NEVER THE ONLY CHANNEL HERE, and every
 * component below holds up its end of it: the mood ring names and counts each slice in the
 * list beside it, every ranked bar prints its own figure at the end, the heat ramp darkens
 * monotonically so it survives greyscale, and a stacked column carries a legend. A chart
 * added to this file that reads only by hue is the one to argue with.
 *
 * THE PRIMARY FILL IS THE PRODUCT'S ACCENT, which used to be forbidden. The old rule was
 * that chrome and data never share a hue so that nobody tries to click a chart; what
 * separates the two now is form and position, and the note at the top of styles.css carries
 * the argument. Practically: `band-handled` and `--color-accent` are the same violet, so a
 * ranked bar and a link are the same colour and only one of them moves under the pointer.
 *
 * FOUR BANDS, NOT ONE PER GROUP. An agent with fourteen outcome labels cannot be drawn
 * as fourteen colours; nobody can read a fourteen-colour stack and no palette separates
 * that many hues. Every group folds into one of four bands an owner actually reasons in:
 * won, handled, support, everything else. The precise label is on the calls list, one
 * click away, where it belongs and where it is a word rather than a colour.
 */

import type { ReactNode } from "react";
import { Search } from "./components/Icons";
import { Quiet } from "./components/States";
import { cx } from "./ui";

// --- the bands ------------------------------------------------------------------------

export type Band = "won" | "handled" | "support" | "other";

/** Fixed order, never cycled. The legend, the stack and the tooltip all read it. */
export const BANDS: Band[] = ["won", "handled", "support", "other"];

/** The one place a band becomes a class. Nothing else in the app names a band colour. */
const BAND_FILL: Record<Band, string> = {
  won: "bg-band-won",
  handled: "bg-band-handled",
  support: "bg-band-support",
  other: "bg-band-other",
};

/**
 * Which band an outcome group charts under.
 *
 * `successGroups` comes from the BUSINESS'S taxonomy, so a boat shop's `qualified` and a
 * dentist's `booked` both land in `won` off the same build. Everything unrecognised
 * lands in `other` rather than getting a colour picked by a hash of its name: a grey
 * band reads as "we do not know what this means", which is true, and a randomly
 * coloured one reads as a meaning nobody assigned.
 */
export function bandOf(group: string | null, successGroups: string[]): Band {
  if (!group) return "other";
  if (successGroups.includes(group)) return "won";
  if (group === "self_served" || group === "referred" || group === "converted") return "handled";
  if (group === "support") return "support";
  return "other";
}

export const BAND_LABELS: Record<Band, string> = {
  won: "Won",
  handled: "Handled",
  support: "Support",
  other: "Everything else",
};

// --- the pieces a chart is made of ------------------------------------------------------

/**
 * A chart with a title and one sentence saying what it means.
 *
 * THE CAPTION IS NOT DECORATION. A chart without a sentence under it is a chart an owner
 * scrolls past: they are not going to work out what a stacked column of outcome bands is
 * telling them, and they should not have to. Every call site passes one, and a chart
 * added without one should look wrong.
 */
export function Figure({
  title,
  caption,
  aside,
  enough = true,
  children,
}: {
  title: string;
  caption: ReactNode;
  /**
   * A right-aligned second thing on the title row: a legend, a link into settings, the
   * timezone every time on the chart is printed in.
   */
  aside?: ReactNode;
  /**
   * False when the window is too thin for the picture to mean anything, and the caption
   * becomes the whole panel.
   *
   * This is the caption rule taken to its conclusion. If the sentence is where the
   * insight lives and the chart is only evidence for it, then a chart with no shape -
   * one bar at 100%, a heatmap with a single lit square, a trend of one column - is
   * evidence of nothing, and drawing it anyway is how a quiet week turns into a screen
   * full of furniture. Use `hasShape` to decide.
   */
  enough?: boolean;
  children: ReactNode;
}) {
  return (
    /*
      A FLEX COLUMN WITH ONE GAP, rather than a margin on each part. The three pieces -
      title row, picture, caption - are spaced identically whichever of them is present,
      and the suppressed-chart case below needs no margin of its own to compensate.
    */
    <section className="flex flex-col gap-[18px] rounded-xl border border-line bg-paper px-[28px] pt-6 pb-[26px]">
      <div className="flex flex-wrap items-baseline justify-between gap-3">
        <h2 className="text-lg font-semibold tracking-[-0.01em]">{title}</h2>
        {aside}
      </div>
      {/*
        `empty:hidden` and not a `children &&` test, because this file cannot run that
        test. `{children}` is a truthy React element whether `BarList` draws eight bars or
        returns null at its own empty guard, so nothing here can tell the two apart - but
        the BROWSER can, because a component that renders null emits no node and this
        wrapper is genuinely `:empty`. `display:none` also takes the element out of the
        flex flow, so the column's gap is not paid for a box with nothing in it. That was
        the original bug in its margin-collapsing form: 18px of blank between title and
        caption where the intended gap was 4px. The call sites now pass `enough` for the
        cases we can predict; this catches the ones we cannot.
      */}
      {enough && <div className="empty:hidden">{children}</div>}
      {/* Set as prose rather than as a footnote: an owner reads the sentence and looks at
          the chart to check it. */}
      {enough ? (
        <p className="max-w-[64ch] text-sm leading-normal text-ink-soft text-pretty">{caption}</p>
      ) : (
        /*
          A SUPPRESSED CHART GETS A BOX, AND EVERY SUPPRESSED CHART GETS THE SAME ONE.
          Without it a thin window is a column of headings each with one sentence adrift
          underneath, which reads as a page that failed to load rather than as a page that
          answered. The box says the sentence IS the panel. One treatment rather than a
          per-panel flag, because two ways of saying "not enough yet" on one screen is the
          reader's problem and not the author's.
        */
        <div className="flex items-center gap-3.5 rounded-md border border-dashed border-line-strong bg-ground px-4 py-3.5">
          <span className="grid size-9 flex-none place-items-center rounded-md bg-sunken text-accent">
            <Search size={16} />
          </span>
          <p className="text-sm leading-normal text-ink-soft text-pretty">{caption}</p>
        </div>
      )}
    </section>
  );
}

/**
 * Whether a breakdown has enough in it to be worth drawing.
 *
 * TWO CONDITIONS, AND BOTH MATTER. A single category is a bar at 100% however many calls
 * are behind it - a picture of the number one - and three calls spread over three
 * categories is noise that looks like a distribution. An owner reading either of those
 * learns nothing and loses trust in the ones that do mean something.
 */
export function hasShape(calls: number, categories: number, min = 3): boolean {
  return calls >= min && categories >= 2;
}

/** A note under a figure that qualifies it - default hours, calls with no local time. */
export function FigureNote({ children }: { children: ReactNode }) {
  return <p className="mt-2 block text-xs text-ink-faint">{children}</p>;
}

/**
 * Text wears text tokens, never the series colour.
 *
 * The swatch beside a legend entry carries the identity; a label painted in its series
 * colour is unreadable at 12px.
 */
export function Legend({ bands }: { bands: Band[] }) {
  return (
    <ul className="flex list-none flex-wrap gap-x-3.5 gap-y-1 p-0 text-xs text-ink-soft">
      {bands.map((band) => (
        <li key={band} className="flex items-center gap-1.5">
          <Swatch className={cx(SWATCH, BAND_FILL[band])} />
          {BAND_LABELS[band]}
        </li>
      ))}
    </ul>
  );
}

/**
 * A block of series colour.
 *
 * IT CARRIES NO SIZE OF ITS OWN, deliberately. Two call sites want two sizes - a legend
 * entry and the 10px block beside a mood - and a default here plus an override at the call
 * site would be two `size-*` utilities in the same group, where which one wins is decided by
 * the order Tailwind emits them rather than the order they were written. `SWATCH` is the
 * shared string for the sites that agree.
 */
function Swatch({ className }: { className: string }) {
  return <span aria-hidden="true" className={cx("flex-none", className)} />;
}

/** The legend's swatch, at the one size a legend uses. */
const SWATCH = "size-2.25 rounded-[2.5px]";

export interface BarDatum {
  key: string;
  label: string;
  /**
   * What the bar's LENGTH means. Always a raw magnitude in one unit, because the bars
   * are compared to each other and two rows measured in different units cannot be.
   */
  value: number;
  /**
   * What the number beside the bar READS as, when the magnitude is not something a
   * person says out loud. A duration ranks by milliseconds and reads as "3m 21s";
   * printing the milliseconds would be showing the reader the implementation.
   */
  display?: string;
  /** Shown under the label - a verbatim example, a location, a second figure. */
  note?: string | null;
  href?: string;
  /**
   * Grey rather than the accent, for a row whose BAR is not the fact worth reading.
   *
   * One caller, and it is the reason this exists rather than a general dial: "where
   * enquiries fall down" draws two numbers per row that must not be added together, and a
   * row where the note's figure is the larger one has a bar that under-reports it. Grey
   * says "the length is not the story here"; the caption says which figure is.
   */
  tone?: "accent" | "muted";
}

/**
 * A ranked list of bars.
 *
 * The right form for "what did callers want" and "what could the agent not answer":
 * these are magnitudes over an unordered set of categories, read by comparing lengths
 * against each other and not against a scale. Horizontal because the labels are words
 * rather than dates, and a vertical bar chart of word-labelled categories is a chart
 * with its labels turned sideways.
 *
 * One series, so no legend - the title names it - and the value is direct-labelled at
 * the end of each bar rather than read off an axis.
 */
export function BarList({
  data,
  total,
  unit = "calls",
  empty,
  max,
  layout = "inline",
  onSelect,
}: {
  data: BarDatum[];
  /**
   * WHICH OF TWO ROW SHAPES, and they are two rather than one because they answer
   * different questions.
   *
   * `inline` is the ranked list: label, bar and figure on one line, so the eye runs down
   * the bars and compares lengths. It is the right shape when the label is short and the
   * comparison IS the panel.
   *
   * `stacked` puts the figure beside the label, the bar full width under both, and the
   * note under that. It costs three times the height per row and buys a note that is a
   * sentence rather than a fragment - which is what "where enquiries fall down" needs,
   * because there the note carries the second figure and nobody can read it truncated into
   * a 200px column.
   */
  layout?: "inline" | "stacked";
  /** The denominator for the share. Defaults to the largest bar. */
  total?: number;
  /** Plural, as it is written nine times out of ten. The tail below singularises it. */
  unit?: string;
  /**
   * How many bars to draw before folding the rest into a line.
   *
   * A RANKED LIST IS READ FROM THE TOP AND ABANDONED PART-WAY DOWN. Fourteen categories
   * is a table, not a chart: nobody compares the eleventh bar to the twelfth, and the
   * tail is what makes one panel three times the height of the one beside it. The rest
   * is counted rather than dropped, so the total still adds up.
   */
  max?: number;
  /**
   * Optional, and usually omitted. Every figure carries a caption that already says what
   * an empty chart means - "the agent answered everything it was asked" - and a second
   * sentence inside the empty chart saying the same thing in different words reads as
   * two different facts.
   */
  empty?: string;
  onSelect?: (key: string) => void;
}) {
  if (!data.length) return empty ? <Quiet>{empty}</Quiet> : null;
  // The peak is taken over EVERYTHING, not just the rows drawn: scaling the bars to the
  // visible subset would redraw the same data at a different length as soon as the cap
  // changed, which is a chart that disagrees with itself between two screens.
  const peak = Math.max(1, ...data.map((row) => row.value));
  const denominator = total ?? peak;
  const shown = max ? data.slice(0, max) : data;
  const rest = data.slice(shown.length);
  const restCalls = rest.reduce((sum, row) => sum + row.value, 0);
  const stacked = layout === "stacked";

  return (
    <ul className={cx("grid list-none p-0", stacked ? "gap-0" : "gap-px")}>
      {shown.map((row, index) => {
        const share = denominator ? Math.round((row.value / denominator) * 100) : 0;
        const width = `${Math.max(2, Math.round((row.value / peak) * 100))}%`;
        const fill = row.tone === "muted" ? "bg-band-other" : "bg-band-handled";
        const figure = (
          <span className="nums font-mono text-base font-semibold whitespace-nowrap">
            {row.display ?? row.value}
          </span>
        );
        return (
          <li
            key={row.key}
            /* The rule between rows belongs to the LIST, not to the row, so the first row
               has none and the panel's own top padding is not doubled. */
            className={cx(stacked && index > 0 && "border-t border-line-soft")}
          >
            <button
              type="button"
              onClick={onSelect ? () => onSelect(row.key) : undefined}
              disabled={!onSelect}
              className={cx(
                "w-full text-left",
                stacked
                  ? "flex flex-col gap-2 py-[14px]"
                  : /* A FRACTION WITH A FLOOR, NOT THE DESIGN'S FLAT 200px. The same list
                       is drawn in both columns of a 7-to-5 split, and 200px of names inside
                       the narrow one leaves about 150px of track for eight bars to be
                       compared in. `0.5fr` gives the names a third of the row whichever
                       column they land in, and the floor keeps a short word from collapsing
                       the column on a phone. */
                    "grid grid-cols-[minmax(90px,0.62fr)_minmax(0,1fr)_36px] items-center gap-5 rounded-sm px-2 py-[9px]",
                onSelect && "cursor-pointer hover:bg-sunken",
              )}
              title={`${row.label} — ${row.display ?? `${row.value} ${unit}`}${
                total ? ` (${share}% of ${total})` : ""
              }`}
            >
              {stacked ? (
                <span className="flex items-baseline justify-between gap-3">
                  <span className="text-base font-medium">{row.label}</span>
                  {figure}
                </span>
              ) : (
                <span className="grid min-w-0 text-base font-medium">
                  {/* One line here too. A two-line category name makes its row taller than
                      the one above it, and a ranked list whose rows are different heights
                      is one the eye cannot run down. */}
                  <span className="truncate">{row.label}</span>
                  {/* ONE LINE, TRUNCATED, and this reverses an earlier decision here. It
                      used to wrap to two, on the argument that the note is a verbatim
                      question and an ellipsis hides the only thing on the row that is not
                      a number. What changed is the column: a fixed 200px name column is
                      what lets the eye run straight down the bars, and a row that is
                      sometimes one line and sometimes two takes that away. The full text
                      is still in the row's `title`, and every tracked question is printed
                      whole on the settings screen this panel links to. The `stacked`
                      layout below is where a note that has to be READ belongs. */}
                  {row.note && (
                    <span className="truncate text-xs leading-snug font-normal text-ink-faint">
                      {row.note}
                    </span>
                  )}
                </span>
              )}

              <span
                className={cx(
                  "block overflow-hidden bg-sunken",
                  stacked ? "h-[6px] w-full rounded-[3px]" : "h-2 rounded-[4px]",
                )}
              >
                <span
                  className={cx("block h-full", fill, stacked ? "rounded-[3px]" : "rounded-[4px]")}
                  style={{ width }}
                />
              </span>

              {stacked
                ? row.note && (
                    <span className="text-xs leading-snug text-ink-faint">{row.note}</span>
                  )
                : figure}
            </button>
          </li>
        );
      })}
      {rest.length > 0 && (
        <li className={cx("text-xs text-ink-faint", stacked ? "pt-3.5" : "px-2 pt-2")}>
          and {rest.length} more, {restCalls.toLocaleString()}{" "}
          {restCalls === 1 ? unit.replace(/s$/, "") : unit} between them
        </li>
      )}
    </ul>
  );
}

export interface DayStack {
  day: number;
  bands: Record<Band, number>;
  total: number;
}

/**
 * Outcomes by day, as stacked columns.
 *
 * Stacked rather than grouped because the question is "what did the days look like",
 * which is a question about composition and about the total at once - and a grouped
 * chart answers neither well at 30 columns. A 2px gap between segments, which is what
 * keeps two adjacent bands legible when one of them is a single call.
 *
 * Days with no calls are drawn as gaps rather than skipped. A chart that omits the
 * quiet days compresses a fortnight of nothing into a week that looks busy.
 *
 * THE COLUMN IS CAPPED AT 36px. An agent with three days of history would otherwise get
 * three slabs a third of the panel wide, which makes a quiet week look like a busy one -
 * a bar's WIDTH carries no meaning here, only its height.
 */
/* --- the three moods -------------------------------------------------------------------- */

/**
 * How the caller sounded, as a part-to-whole ring.
 *
 * A DONUT, WHERE EVERYTHING ELSE ON THIS PAGE IS A BAR, AND THE FORM IS ARGUED RATHER THAN
 * PREFERRED. `BarList` is right for intents and outcomes because those are
 * magnitudes over an UNORDERED, OPEN-ENDED set read by comparing lengths - twenty
 * categories where the question is which is longest. This is the opposite shape: a closed
 * set of exactly three that always sums to every call read, where the question is what
 * FRACTION each is. That is the one question a ring answers better than a bar, and three
 * slices is inside the handful where the eye can still judge an angle. A fourth category
 * would be the point to stop and reach for the bars.
 *
 * `pathLength={100}` IS WHY THERE IS NO ARITHMETIC HERE. It renormalises the circumference
 * to 100 user units, so `strokeDasharray` is literally "this share, then the rest" and the
 * offset is a running total in percent. Computing 2πr instead means a magic number that
 * has to change with the radius, and a rounding error that shows up as a hairline gap
 * between two slices at some window widths and not others. The radius DID change - the
 * viewBox is now the ring's real pixel size so the design's 18px band is 18 here too - and
 * nothing in the arithmetic below moved, which is the property being bought.
 *
 * THE TOTAL IN THE MIDDLE IS A `div`, NOT AN SVG `text`. The `svg` carries `-rotate-90` so
 * the ring starts at twelve o'clock, and a `text` inside it would inherit that and print
 * the number on its side. Overlaying it also means the figure is set in the real UI face
 * rather than in whatever an `svg` resolves `font-family` to.
 *
 * The `-90deg` rotation starts the ring at twelve o'clock, which is where a reader assumes
 * a part-to-whole chart starts; SVG's own zero is three o'clock.
 *
 * ONE SLICE AT A HUNDRED PERCENT IS A FULL RING RATHER THAN A DEGENERATE PATH, which is
 * the other reason for circles over arc paths - though `hasShape` should have kept that
 * case off the screen before it reaches here.
 */
export type Mood = "positive" | "neutral" | "negative";

/** Fixed order, best to worst. Read by the ring, the legend and the drill-through alike. */
export const MOODS: Mood[] = ["positive", "neutral", "negative"];

export const MOOD_LABELS: Record<Mood, string> = {
  positive: "Positive",
  neutral: "Neutral",
  negative: "Negative",
};

/** The one place a mood becomes a colour. Nothing else in the app names one. */
const MOOD_STROKE: Record<Mood, string> = {
  positive: "stroke-band-won",
  neutral: "stroke-band-other",
  negative: "stroke-band-negative",
};

/**
 * EXPORTED so the sentiment cards in Settings can wear the same three colours the donut
 * paints, rather than a second set picked to look similar. This is still "the one place a
 * mood becomes a colour" - the settings card reads it, it does not name its own.
 */
export const MOOD_FILL: Record<Mood, string> = {
  positive: "bg-band-won",
  neutral: "bg-band-other",
  negative: "bg-band-negative",
};

/*
 * THE SAME THREE MOODS, FOR SOMETHING THAT IS WORDS RATHER THAN A SHAPE.
 *
 * `MOOD_FILL` cannot be borrowed for text and this is the note that says why. Those three
 * are chart FILLS, chosen to separate as areas: against paper they measure 3.0:1, 2.0:1 and
 * 3.9:1, so all three fail AA the moment they are set on a word. `Sentiments.tsx` already
 * knows this - it uses `MOOD_FILL` for an 8px dot and nothing else.
 *
 * So the sentiment card on a call reads from these instead, and they are the meaning tokens
 * the rest of the product already uses for the same three feelings: `good`, `cold` - which
 * `styles.css` describes as "nothing in particular", which is exactly what neutral is - and
 * `alert`. Every pair here is already checked by `scripts/check-contrast.mjs`.
 *
 * THEY LIVE HERE, BESIDE THE FILLS, so this file is still the only place a mood becomes a
 * colour. A card in `CallReading.tsx` naming its own green is the second green, and the
 * two drift within a month.
 */
export const MOOD_INK: Record<Mood, string> = {
  positive: "text-good",
  neutral: "text-cold",
  negative: "text-alert",
};

/** The ground each of those inks was checked against. Border included, so they stay a pair. */
export const MOOD_TINT: Record<Mood, string> = {
  positive: "bg-good-soft border-good-line",
  neutral: "bg-cold-soft border-cold-line",
  negative: "bg-alert-soft border-alert-line",
};

export function isMood(key: string | null): key is Mood {
  return !!key && (MOODS as string[]).includes(key);
}

export interface MoodSlice {
  mood: Mood;
  calls: number;
}

export function SentimentPie({
  slices,
  total,
  onSelect,
}: {
  slices: MoodSlice[];
  /** Calls that were READ, not calls answered. See the panel's note in Overview.tsx. */
  total: number;
  onSelect?: (mood: Mood) => void;
}) {
  if (!total) return null;

  // The running start angle, in the same percent units `pathLength` put the ring in.
  let offset = 0;
  const drawn = MOODS.map((mood) => {
    const calls = slices.find((slice) => slice.mood === mood)?.calls ?? 0;
    const share = (calls / total) * 100;
    const at = offset;
    offset += share;
    return { mood, calls, share, at };
  });

  return (
    <div className="grid grid-cols-[150px_minmax(0,1fr)] items-center gap-8 max-[560px]:grid-cols-1">
      <div className="relative size-[150px] flex-none">
        <svg
          viewBox="0 0 150 150"
          className="size-full -rotate-90"
          role="img"
          aria-label={drawn
            .map((slice) => `${MOOD_LABELS[slice.mood]} ${Math.round(slice.share)}%`)
            .join(", ")}
        >
          {/* The track, so a ring whose slices do not quite reach 100 from rounding still
              reads as a closed circle rather than as a missing wedge. */}
          <circle cx="75" cy="75" r="66" fill="none" className="stroke-line-soft" strokeWidth="18" />
          {drawn.map((slice) =>
            slice.calls ? (
              <circle
                key={slice.mood}
                cx="75"
                cy="75"
                r="66"
                fill="none"
                pathLength={100}
                strokeWidth="18"
                strokeDasharray={`${slice.share} ${100 - slice.share}`}
                strokeDashoffset={-slice.at}
                className={MOOD_STROKE[slice.mood]}
              >
                <title>{`${MOOD_LABELS[slice.mood]} — ${slice.calls} of ${total}`}</title>
              </circle>
            ) : null,
          )}
        </svg>
        {/* The denominator, printed where nobody can miss it. The panel's whole caveat is
            that this is calls READ and not calls answered, and a ring with the number in
            the middle of it says so before the sentence underneath has to. */}
        <div
          aria-hidden="true"
          className="absolute inset-0 flex flex-col items-center justify-center gap-0.5"
        >
          <span className="nums text-3xl leading-none font-medium tracking-[-0.03em]">{total}</span>
          <span className="text-2xs text-ink-faint">calls read</span>
        </div>
      </div>

      {/*
        DIRECT-LABELLED, AND THAT IS NOT DECORATION HERE. This is the one figure in the
        product carrying a red and a green, so the word and the number beside each swatch
        are what make it readable to somebody who cannot separate the two hues - the
        colour is never the only carrier. It is also why the rows are a list rather than
        labels on the ring: three angles annotated in place are unreadable at this size.
      */}
      <ul className="grid min-w-0 list-none p-0">
        {drawn.map((slice, index) => (
          /* The rule sits on the `li` and the hover wash on the `button` inside it. The
             other way round leaves a notch where the washed row meets its own border. */
          <li key={slice.mood} className={cx(index > 0 && "border-t border-line-soft")}>
            <button
              type="button"
              onClick={onSelect && slice.calls ? () => onSelect(slice.mood) : undefined}
              disabled={!onSelect || !slice.calls}
              className={cx(
                "grid w-full grid-cols-[minmax(0,1fr)_40px_56px] items-center gap-3",
                "py-[12px] text-left text-base",
                onSelect && slice.calls > 0 && "cursor-pointer",
              )}
            >
              <span className="flex min-w-0 items-center gap-2.5 font-medium">
                <Swatch className={cx("size-2.5 rounded-[3px]", MOOD_FILL[slice.mood])} />
                <span className="truncate">{MOOD_LABELS[slice.mood]}</span>
              </span>
              <span className="nums font-mono text-right font-semibold">{slice.calls}</span>
              <span className="nums font-mono text-right text-sm text-ink-faint">
                {Math.round(slice.share)}%
              </span>
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

/**
 * One number out of a hundred, drawn as an arc.
 *
 * SAME CONSTRUCTION AS `SentimentPie` ABOVE, and that is the reason it is in this file
 * rather than in the call dialog that wants it. A stroked `circle` with `pathLength={100}`,
 * the `svg` rotated so the arc starts at twelve o'clock, and the figure overlaid as a `div`
 * because an SVG `text` would inherit that rotation and print on its side. Two rings in one
 * product built two different ways is two ring widths and two start angles within a month.
 *
 * NOT A `conic-gradient`, which is the obvious one-line answer and is wrong twice over. A
 * conic gradient paints a DISC, so the hole has to be faked with an inner circle in the
 * card's own background colour - which breaks the moment the card is tinted or moves onto
 * `sunken` - and it is a background image, so it has no accessibility surface at all. The
 * arc below gets `role="img"` and a real label for free.
 *
 * THE TRACK IS `line-soft` AND THE ARC IS THE ACCENT, matching the number beside it rather
 * than the heat ramp. `Qualification.tsx` argues the meter's four steps are ORDINAL and get
 * one hue light-to-dark; this is a single continuous quantity with no steps to encode, so
 * it is the one accent the score is already set in.
 */
export function ScoreRing({
  score,
  size = 84,
  label,
}: {
  /** 0-100. Anything outside is clamped rather than drawn as an over-full ring. */
  score: number;
  size?: number;
  /** What the arc means, for a screen reader. The visible figure is `aria-hidden`. */
  label: string;
}) {
  const share = Math.max(0, Math.min(100, score));
  // Proportional to the box, so the one caller that wants a different size gets a ring that
  // looks like this one rather than a hoop or a doughnut. `SentimentPie` learnt this the
  // hard way: its radius changed once and no arithmetic moved with it.
  const stroke = size * 0.1;
  const radius = 50 - (stroke / size) * 50;

  return (
    <div className="relative flex-none" style={{ width: size, height: size }}>
      <svg viewBox="0 0 100 100" className="size-full -rotate-90" role="img" aria-label={label}>
        <circle
          cx="50"
          cy="50"
          r={radius}
          fill="none"
          className="stroke-line-soft"
          strokeWidth={(stroke / size) * 100}
        />
        {share > 0 && (
          <circle
            cx="50"
            cy="50"
            r={radius}
            fill="none"
            pathLength={100}
            strokeWidth={(stroke / size) * 100}
            strokeDasharray={`${share} ${100 - share}`}
            strokeLinecap="round"
            className="stroke-accent"
          />
        )}
      </svg>
      <div
        aria-hidden="true"
        className="absolute inset-0 flex flex-col items-center justify-center"
      >
        <span className="nums font-mono text-2xl leading-none font-semibold tracking-[-0.03em] text-accent">
          {score}
        </span>
        <span className="nums mt-0.5 font-mono text-2xs text-ink-faint">/100</span>
      </div>
    </div>
  );
}

export function StackedDays({
  days,
  bands,
  onSelect,
}: {
  days: DayStack[];
  bands: Band[];
  onSelect?: (day: number) => void;
}) {
  if (!days.length) return <Quiet>No calls in this window.</Quiet>;
  const peak = Math.max(1, ...days.map((d) => d.total));

  return (
    <>
      <div className="flex h-[120px] items-end gap-[4px]">
        {days.map((day) => (
          <button
            key={day.day}
            type="button"
            onClick={onSelect ? () => onSelect(day.day) : undefined}
            disabled={!onSelect}
            className={cx(
              "flex h-full max-w-9 flex-1 items-end",
              onSelect && "cursor-pointer",
            )}
            title={`${dayLabel(day.day)} — ${day.total} call${day.total === 1 ? "" : "s"}${
              day.total
                ? `: ${bands
                    .filter((b) => day.bands[b])
                    .map((b) => `${day.bands[b]} ${BAND_LABELS[b].toLowerCase()}`)
                    .join(", ")}`
                : ""
            }`}
          >
            <span
              className="flex min-h-0.5 w-full flex-col-reverse gap-0.5 overflow-hidden rounded-t-[6px] rounded-b-[3px]"
              style={{ height: `${(day.total / peak) * 100}%` }}
            >
              {bands.map((band) =>
                day.bands[band] ? (
                  <span
                    key={band}
                    className={cx("block min-h-0.5", BAND_FILL[band])}
                    style={{ flexGrow: day.bands[band] }}
                  />
                ) : null,
              )}
            </span>
          </button>
        ))}
      </div>
      {/* The first and last day only. A label under every column is unreadable at thirty
          of them and redundant at three - the two ends say what period is on screen, and
          hovering a column says which day it is. */}
      <div className="mt-2 flex justify-between text-2xs text-ink-faint">
        <span>{dayLabel(days[0]!.day)}</span>
        {days.length > 1 && <span>{dayLabel(days[days.length - 1]!.day)}</span>}
      </div>
    </>
  );
}

/**
 * When people call: seven rows of twenty-four squares.
 *
 * A SEQUENTIAL RAMP, LIGHT TO DARK, and it is sequential by LIGHTNESS rather than by hue.
 * It used to be one blue at four saturations, on the argument that the thing encoded is a
 * magnitude and not an identity. It is now pale grey, mid grey, the accent, then ink, and
 * the argument survives in the form that matters: relative luminance falls 0.89, 0.67,
 * 0.25, 0.15, 0.01, so the ramp still reads in order with the colour taken out.
 *
 * ITS WEAK LINK IS STEPS 2 TO 3 - mid grey to the accent, 1.53:1 - where the separation is
 * carried by chroma rather than by lightness. Two things already in this component cover
 * that: every square names its own exact count in a `title`, and the legend below is a ramp
 * captioned "Quieter" to "Busier" rather than a key of four labels. Nobody has to tell step
 * 2 from step 3 to read this chart; they have to find the dark block.
 *
 * Four steps rather than a continuous scale: an owner reads this to find the busy block,
 * not to tell nine calls from ten, and four discrete steps with a legend are read at a
 * glance where a gradient is read by squinting at a colour bar.
 *
 * A square with no calls is empty rather than the palest step. "Nobody rang at 4am on a
 * Tuesday" and "one person did" are different facts and the chart has to show both.
 *
 * `heat-grid` and `heat-cell` are classes rather than utilities because every row has to
 * share one `grid-template-columns` or the columns do not line up - see styles.css.
 */
export function Heatmap({
  grid,
  isOpen,
}: {
  grid: Array<{ dow: number; hour: number; calls: number }>;
  /** Draws the outline that says which squares were inside opening hours. */
  isOpen: (dow: number, hour: number) => boolean;
}) {
  const byKey = new Map(grid.map((cell) => [`${cell.dow}:${cell.hour}`, cell.calls]));
  const peak = Math.max(1, ...grid.map((cell) => cell.calls));
  const DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

  return (
    <div className="grid gap-[6px] overflow-x-auto text-2xs">
      <div className="heat-grid font-mono text-ink-faint" aria-hidden="true">
        {[0, 6, 12, 18].map((hour) => (
          <span key={hour} style={{ gridColumn: hour + 2 }}>
            {clock(hour)}
          </span>
        ))}
      </div>
      {DAYS.map((name, dow) => (
        <div className="heat-grid" key={dow}>
          <span className="text-xs text-ink-faint">{name}</span>
          {Array.from({ length: 24 }, (_, hour) => {
            const calls = byKey.get(`${dow}:${hour}`) ?? 0;
            return (
              <span
                key={hour}
                className={cx("heat-cell", calls > 0 && `s${step(calls, peak)}`, isOpen(dow, hour) && "open")}
                title={`${name} ${clock(hour)} — ${calls} call${calls === 1 ? "" : "s"}${
                  isOpen(dow, hour) ? "" : " (outside opening hours)"
                }`}
              />
            );
          })}
        </div>
      ))}
      <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-2 text-xs text-ink-faint">
        <span className="flex items-center gap-1.5">
          Quieter
          {[1, 2, 3, 4].map((s) => (
            <span key={s} className={`heat-cell s${s} w-[14px]`} aria-hidden="true" />
          ))}
          Busier
        </span>
        <span className="ml-auto flex items-center gap-1.5">
          {/* White with the ring, not the empty-cell grey: this swatch is showing the
              OUTLINE, and drawing it on a filled square says the fill is part of it. */}
          <span className="heat-cell open w-[14px] bg-paper" aria-hidden="true" /> inside opening
          hours
        </span>
      </div>
    </div>
  );
}

function step(calls: number, peak: number): 1 | 2 | 3 | 4 {
  const share = calls / peak;
  if (share > 0.75) return 4;
  if (share > 0.5) return 3;
  if (share > 0.25) return 2;
  return 1;
}

/**
 * Calls per week, with the business's own good outcome drawn inside them.
 *
 * ONE AXIS. Both series are counts of calls, so they share a scale and the second is
 * drawn inside the first rather than beside it - which is what makes "of the calls we
 * took, this many went well" readable as a proportion. A second y-axis for the value in
 * dollars would be the obvious next step and is the single most common way a chart lies:
 * the crossing point of two arbitrarily-scaled lines means nothing. Money gets its own
 * figure.
 */
export function WeeklyColumns({
  weeks,
  successLabel,
}: {
  weeks: Array<{ week: number; calls: number; success: number }>;
  successLabel: string;
}) {
  if (!weeks.length) return <Quiet>No calls in this window.</Quiet>;
  const peak = Math.max(1, ...weeks.map((w) => w.calls));

  /*
    THE HEIGHTS ARE PIXELS, NOT PERCENTAGES, and that is what lets the count sit ABOVE the
    bar. A percentage height resolves against the flex column that also holds the label, so
    the tallest bar would push its own number off the top of the panel. A track measured in
    px is measured against nothing, and `items-end` on the grid lines the columns up on
    their feet.
  */
  const TRACK = 96;
  const columns = { gridTemplateColumns: `repeat(${weeks.length}, minmax(0, 1fr))` };

  return (
    <>
      <div className="mb-2 grid items-end gap-2" style={columns}>
        {weeks.map((week) => (
          <div
            key={week.week}
            /* Capped, so an agent with two weeks of history gets two columns rather than
               two enormous slabs. A bar's WIDTH carries no meaning here, only its height. */
            className="mx-auto flex w-full max-w-14 flex-col items-center gap-1.5"
            title={`Week of ${dayLabel(week.week)} — ${week.calls} calls, ${week.success} ${successLabel}`}
          >
            <span
              className={cx(
                "nums font-mono text-xs font-semibold",
                week.calls ? "text-ink" : "text-ink-faint",
              )}
            >
              {week.calls}
            </span>
            {week.calls ? (
              <span
                className="flex w-full flex-col-reverse overflow-hidden rounded-t-[6px] rounded-b-[3px] bg-band-other"
                style={{ height: Math.max(6, Math.round((week.calls / peak) * TRACK)) }}
              >
                <span
                  className="block w-full bg-band-won"
                  style={{ height: `${(week.success / week.calls) * 100}%` }}
                />
              </span>
            ) : (
              /* A week nobody rang in is a stub rather than nothing: the column has to stay
                 on the axis or the row of labels below stops lining up with anything. */
              <span className="h-[4px] w-full rounded-[2px] bg-line" />
            )}
          </div>
        ))}
      </div>
      <div className="mb-3 grid gap-2 text-2xs text-ink-faint" style={columns}>
        {weeks.map((week) => (
          <span key={week.week} className="truncate text-center">
            {shortDay(week.week)}
          </span>
        ))}
      </div>
      <ul className="flex list-none flex-wrap gap-x-3.5 gap-y-1 p-0 text-xs text-ink-soft">
        <li className="flex items-center gap-1.5">
          <Swatch className={cx(SWATCH, "bg-band-other")} />
          Calls
        </li>
        <li className="flex items-center gap-1.5">
          <Swatch className={cx(SWATCH, "bg-band-won")} />
          {successLabel}
        </li>
      </ul>
    </>
  );
}

// --- formatting the day keys ------------------------------------------------------------
//
// The day key is a CIVIL DATE - the business's calendar date, stored as that date read as
// UTC midnight - so it is formatted in UTC. Formatting it in the reader's local timezone
// would shift every label by a day for anyone reading the dashboard from another
// country, which is the exact bug the civil-date key exists to avoid.

const DAY_FORMAT = new Intl.DateTimeFormat(undefined, {
  weekday: "short",
  day: "numeric",
  month: "short",
  timeZone: "UTC",
});
const SHORT_FORMAT = new Intl.DateTimeFormat(undefined, {
  day: "numeric",
  month: "short",
  timeZone: "UTC",
});

export function dayLabel(day: number): string {
  return DAY_FORMAT.format(new Date(day));
}

export function shortDay(day: number): string {
  return SHORT_FORMAT.format(new Date(day));
}

function clock(hour: number): string {
  if (hour === 0) return "12am";
  if (hour === 12) return "12pm";
  return hour < 12 ? `${hour}am` : `${hour - 12}pm`;
}

/**
 * Fill in the days nobody called.
 *
 * The API returns only the days that have calls, because a query that returned empty
 * rows would need a calendar table. The chart needs them: a fortnight of silence drawn
 * as a gap is the truth, and drawn as a compressed run of busy days is not.
 */
export function fillDays(
  rows: Array<{ day: number; group: string | null; calls: number }>,
  successGroups: string[],
  window: { since: number; until: number },
): DayStack[] {
  const empty = (): Record<Band, number> => ({ won: 0, handled: 0, support: 0, other: 0 });
  const byDay = new Map<number, DayStack>();

  for (const row of rows) {
    const stack = byDay.get(row.day) ?? { day: row.day, bands: empty(), total: 0 };
    stack.bands[bandOf(row.group, successGroups)] += row.calls;
    stack.total += row.calls;
    byDay.set(row.day, stack);
  }
  if (!byDay.size) return [];

  const days = [...byDay.keys()];
  // Bounded by the data rather than by the window when the window is the longer of the
  // two: ninety empty columns in front of a week of calls is a chart of nothing.
  const DAY_MS = 86_400_000;
  const first = Math.min(...days, floorDay(Math.max(window.since, Math.min(...days))));
  const last = Math.max(...days);

  const out: DayStack[] = [];
  for (let day = first; day <= last; day += DAY_MS) {
    out.push(byDay.get(day) ?? { day, bands: empty(), total: 0 });
  }
  return out;
}

function floorDay(epoch: number): number {
  return Math.floor(epoch / 86_400_000) * 86_400_000;
}
